import type { Motif, MotifComponentProps } from '@snapchat/snap-design-system-marketing';
import {
  ArcadiaBlack,
  ArcadiaGray,
  contrastBlackButtonVars,
  darkThemeHeaderVars,
  defaultMotif,
  FontFamily,
  m,
  mergeMotifs,
  MotifComponent,
  MotifScheme,
} from '@snapchat/snap-design-system-marketing';

const blockVars: Partial<MotifComponentProps<MotifComponent.BLOCK>> = {
  '--block-title-desktop-font-size': '42px',
  '--block-title-desktop-font-stretch': 'normal',
  '--block-title-desktop-font-line-height': '56px',
  '--block-title-mobile-font-size': '32px',
};

const headerSiteNameVars: Partial<MotifComponentProps<MotifComponent.HEADER>> = {
  '--global-header-site-name-font-family': 'inherit',
  '--global-header-site-name-font-size': m('--action-desktop-font-size'),
};

/** Motif for the Arcadia Creative Studios websites. */
export const arcadiaMotif: Motif = mergeMotifs(defaultMotif, {
  name: 'Arcadia',
  fontFamily: [FontFamily.GRAPHIK],
  [MotifScheme.DEFAULT]: {
    name: 'Arcadia Yellow background.',
    [MotifComponent.ROOT]: {
      '--font-family': `${FontFamily.GRAPHIK}, Helvetica, Tahoma, Arial, sans-serif`,
      '--h1-font-family': `${FontFamily.GRAPHIK}, Helvetica, Tahoma, Arial, sans-serif`,
      '--h2-font-family': `${FontFamily.GRAPHIK}, Helvetica, Tahoma, Arial, sans-serif`,
      '--h3-font-family': `${FontFamily.GRAPHIK}, Helvetica, Tahoma, Arial, sans-serif`,
      '--h4-font-family': `${FontFamily.GRAPHIK}, Helvetica, Tahoma, Arial, sans-serif`,
      '--h5-font-family': `${FontFamily.GRAPHIK}, Helvetica, Tahoma, Arial, sans-serif`,
      '--h6-font-family': `${FontFamily.GRAPHIK}, Helvetica, Tahoma, Arial, sans-serif`,
    },
    [MotifComponent.BLOCK]: blockVars,
    [MotifComponent.HEADER]: headerSiteNameVars,
  },
  [MotifScheme.SECONDARY]: {
    name: 'Arcadia Black background.',
    [MotifComponent.ROOT]: {
      '--bg-color': ArcadiaBlack.V200,
      '--gutter-color': ArcadiaBlack.V200,
      '--h2-desktop-font-letter-spacing': 'normal',
      '--h2-desktop-font-line-height': '120%',
      '--h2-desktop-font-size': '48px',
      '--h2-desktop-font-stretch': 'normal',
      '--h2-desktop-font-text-transform': 'none',
      '--h2-mobile-font-letter-spacing': 'normal',
      '--h2-mobile-font-line-height': '120%',
      '--h2-mobile-font-size': '40px',
      '--h2-mobile-font-stretch': 'normal',
      '--h2-mobile-font-text-transform': 'none',
      '--h3-desktop-font-weight': '500',
      '--h3-mobile-font-size': '28px',
      '--h3-mobile-font-weight': '500',
      '--h4-desktop-font-weight': '500',
      '--h4-mobile-font-size': '24px',
      '--h4-mobile-font-weight': '500',
      '--h5-desktop-font-size': '28px',
      '--h5-desktop-font-weight': '300',
      '--h5-mobile-font-size': '24px',
      '--h5-mobile-font-weight': '300',
      '--h6-desktop-font-size': '20px',
      '--h6-desktop-font-weight': '500',
      '--h6-mobile-font-weight': '500',
      '--p1-desktop-font-size': '24px',
      '--p1-desktop-font-line-height': '140%',
      '--p1-desktop-font-weight': '300',
      '--p2-desktop-font-size': '18px',
    },
    [MotifComponent.HEADER]: {
      ...headerSiteNameVars,
      '--global-header-bg-color': ArcadiaBlack.V200,
    },
    [MotifComponent.BLOCK]: {
      ...blockVars,
      '--block-title-color': m('--neutral-v0'),
    },
    [MotifComponent.FORM]: {
      '--form-input-box-shadow': 'none',
      '--form-input-desktop-font-weight': '400',
      '--form-input-mobile-font-weight': '400',
    },
  },
  [MotifScheme.TERTIARY]: {
    name: 'Arcadia White background.',
    [MotifComponent.BLOCK]: blockVars,
    [MotifComponent.HEADER]: headerSiteNameVars,
  },
  [MotifScheme.QUATERNARY]: {
    name: 'Arcadia Gray background. Dark Text',
    [MotifComponent.ROOT]: {
      '--bg-color': ArcadiaGray.V300,
      '--gutter-color': ArcadiaGray.V300,
      '--fg-color': m('--neutral-v700'),
    },
    [MotifComponent.BUTTON]: contrastBlackButtonVars,
    [MotifComponent.HEADER]: {
      ...darkThemeHeaderVars,
      ...headerSiteNameVars,
      '--global-header-bg-color': ArcadiaGray.V300,
    },
    [MotifComponent.BLOCK]: {
      ...blockVars,
      '--block-title-color': m('--primary-v100'),
    },
    [MotifComponent.FOOTER]: {
      '--footer-bg-color': m('--neutral-v150'),
    },
  },
});
